"use client";

import React from "react";
import { cn } from "../../../helpers/classNames";
import { useCountry } from "../../../hooks/useCountry";
import { useIsMobileSafari } from "../../../hooks/useIsMobileSafari";
import { useIsPersistingForbidden } from "../../../hooks/useIsPersistingForbidden";
import { usePageParams } from "../../../hooks/usePageParams";
import { isSpecialCam } from "../../../network/matlocq/helpers/livecams";
import { useLivecam } from "../../../network/matlocq/queries/Livecam";
import { useSkiArea } from "../../../network/matlocq/queries/SkiArea";
import { useConsent, useIsPay } from "../../Consent/hooks";
import type {
    AdSlotsATClickPerformance,
    AdSlotsCHClickPerformance,
    AdSlotsDEClickPerformance,
} from "../types";
import styles from "./styles.module.scss";

type Props = {
    slot: {
        de?: Extract<
            AdSlotsDEClickPerformance,
            "WET_DE_M_overlay_stickybanner" | "WET_DE_M_premiumstickybanner"
        >;
        at?: Extract<
            AdSlotsATClickPerformance,
            "WET_AT_M_overlay_stickybanner"
        >;
        ch?: Extract<
            AdSlotsCHClickPerformance,
            "WET_CH_M_overlay_stickybanner" | "WET_CH_M_premiumstickybanner"
        >;
    };
};

/**
 * **Note:** this component is called directly and not via Ad/slots/index.tsx.
 * The slot id is not passed and static inside the component.
 * @returns React.FC with the sticky ad slot.
 */
export const ClickPerformanceSticky: React.FC<Props> = ({ slot }) => {
    const consent = useConsent();
    const country = useCountry();
    const isPersistingForbidden = useIsPersistingForbidden();
    const isPay = useIsPay();
    const isMobileSafari = useIsMobileSafari();
    const pageParams = usePageParams();
    const { pageType } = pageParams;
    const { data: livecam } = useLivecam(
        pageType?.isLivecamDetailPage && typeof pageParams.id === "string"
            ? pageParams.id
            : null
    );
    const { data: skiArea } = useSkiArea({
        id: typeof pageParams.id === "string" ? pageParams.id : null,
        enabled: pageType?.isAnySkiAreaDetailPage,
    });

    if (isPay || isPersistingForbidden || !consent) {
        return null;
    }

    const hideAds =
        (pageType?.isLivecamDetailPage && livecam && isSpecialCam(livecam)) ||
        (pageType?.isAnySkiAreaDetailPage &&
            skiArea &&
            skiArea.sponsoring.isSponsored);

    if (hideAds) {
        return null;
    }

    const adjustZindexToLightbox =
        (country === "de" && slot.de === "WET_DE_M_overlay_stickybanner") ||
        country === "at" ||
        (country === "ch" && slot.ch === "WET_CH_M_overlay_stickybanner");

    if (
        (country === "de" && slot.de !== undefined) ||
        (country === "at" && slot.at !== undefined) ||
        (country === "ch" && slot.ch !== undefined)
    ) {
        return (
            <div className={cn(styles.adSlot, styles.clickperformanceWrapper)}>
                <div
                    className={cn(
                        styles.stickyBanner,
                        isMobileSafari
                            ? styles.stickyBannerTop
                            : styles.stickyBannerBottom,
                        adjustZindexToLightbox &&
                            styles.stickyBannerInsideLightbox
                    )}
                    id={slot[country] ? `${slot[country]}-outer` : ""}
                    data-testid="cp-adslot"
                >
                    <div
                        id={slot[country]}
                        className={cn(
                            styles.stickyBannerInner,
                            "ad-flex",
                            "ad-fix"
                        )}
                    />
                </div>
            </div>
        );
    } else {
        return null;
    }
};
